import { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { SeasonByIdQuery, SeasonsDocument, useUpdateSeasonMutation } from '../../../../schema';
import { toast } from 'react-toastify';
import { FORM_ERROR, SubmissionErrors } from 'final-form';

export type EditSeasonFormValues = {
  title: string;
  start: string;
  end: string;
  description: string;
  brandColor: string;
  imageId: string;
  currencyIconId: string;
  nominativeSingular: string;
  genitiveSingular: string;
  genitivePlural: string;
  accusativePlural: string;
};

type Props = {
  seasonId: string;
  data: SeasonByIdQuery | undefined;
};

export const useEditSeasonForm = ({ seasonId, data }: Props) => {
  const history = useHistory();

  const [updateSeason, { data: updateData, loading, error }] = useUpdateSeasonMutation();

  const season = data?.season;

  const initialValues: EditSeasonFormValues = {
    title: season?.generalInformation.title ?? '',
    start: season?.generalInformation.start ?? '',
    end: season?.generalInformation.end ?? '',
    description: season?.generalInformation.description ?? '',
    brandColor: season?.generalInformation.brandColor ?? '',
    imageId: season?.generalInformation.image?.id ?? '',
    currencyIconId: season?.currency.icon?.id ?? '',
    nominativeSingular: season?.currency.name.nominativeSingular ?? '',
    genitiveSingular: season?.currency.name.genitiveSingular ?? '',
    genitivePlural: season?.currency.name.genitivePlural ?? '',
    accusativePlural: season?.currency.name.accusativePlural ?? '',
  };

  const onCancel = useCallback(() => {
    history.push(`/battle-pass/seasons/${seasonId}`);
  }, [history, seasonId]);

  const onSubmit = useCallback(
    async (values: EditSeasonFormValues) => {
      const generalInformation = {
        title: values.title,
        start: values.start,
        end: values.end,
        description: values.description,
        brandColor: values.brandColor || null,
        imageId: values.imageId,
      };

      const currency = {
        iconId: values.currencyIconId,
        name: {
          nominativeSingular: values.nominativeSingular,
          genitiveSingular: values.genitiveSingular,
          genitivePlural: values.genitivePlural,
          accusativePlural: values.accusativePlural,
        },
      };

      try {
        await updateSeason({
          variables: {
            input: {
              id: seasonId,
              generalInformation,
              currency,
            },
          },
          refetchQueries: [{ query: SeasonsDocument }],
        });
      } catch {
        toast.error('Ошибка сохранения сезона');
        return { [FORM_ERROR]: 'Ошибка сохранения сезона' } as SubmissionErrors;
      }
    },
    [seasonId, updateSeason]
  );

  useEffect(() => {
    if (updateData !== undefined) {
      toast.success('Сезон успешно сохранён');
      onCancel();
    }
  }, [updateData, onCancel]);

  useEffect(() => {
    if (error) {
      toast.error('Ошибка сохранения сезона');
    }
  }, [error]);

  return {
    initialValues,
    onSubmit,
    onCancel,
    loading,
  };
};
